const MINUTES_PER_HOUR = 60;
const WORK_HOURS_PER_DAY = 8;
const WORK_DAYS_PER_WEEK = 5;

const MINUTES_PER_DAY = WORK_HOURS_PER_DAY * MINUTES_PER_HOUR;
const MINUTES_PER_WEEK = WORK_DAYS_PER_WEEK * MINUTES_PER_DAY;

const WEEKS_PER_YEAR = 52;
const MAX_DURATION_MINUTES = MINUTES_PER_WEEK * WEEKS_PER_YEAR;

const minutesPerUnit: Record<string, number> = {
    m: 1,
    h: MINUTES_PER_HOUR,
    d: MINUTES_PER_DAY,
    w: MINUTES_PER_WEEK,
};

export const defaultDurationPresets: readonly number[] = [
    15,
    30,
    45,
    MINUTES_PER_HOUR,
    2 * MINUTES_PER_HOUR,
    4 * MINUTES_PER_HOUR,
    MINUTES_PER_DAY,
    2 * MINUTES_PER_DAY,
    MINUTES_PER_WEEK,
];

/**
 * Parses free-form duration input like "45m", "1.5h", "1h 30m", "2d" or a
 * bare number of minutes. Returns whole minutes, or undefined when the
 * input is not a valid duration.
 */
export const parseDuration = (input: string): number | undefined => {
    const normalizedInput = input.trim().toLowerCase().replace(/\s+/g, "");
    if (normalizedInput.length === 0) {
        return undefined;
    }
    const tokenPattern = /(\d+(?:\.\d+)?)([mhdw])?/g;
    let totalMinutes = 0;
    let matchedLength = 0;
    const seenUnits = new Set<string>();
    for (const match of normalizedInput.matchAll(tokenPattern)) {
        const [token, amount, unit] = match;
        const unitKey = unit ?? "m";
        if (seenUnits.has(unitKey)) {
            return undefined;
        }
        seenUnits.add(unitKey);
        matchedLength += token.length;
        totalMinutes += Number(amount) * minutesPerUnit[unitKey];
    }
    const roundedMinutes = Math.round(totalMinutes);
    if (
        matchedLength !== normalizedInput.length ||
        roundedMinutes <= 0 ||
        roundedMinutes > MAX_DURATION_MINUTES
    ) {
        return undefined;
    }
    return roundedMinutes;
};

export const formatDuration = (minutes: number | undefined): string => {
    if (minutes === undefined || minutes <= 0) {
        return "";
    }
    const parts: string[] = [];
    let remainingMinutes = Math.round(minutes);
    const unitEntries: ReadonlyArray<[string, number]> = [
        ["w", MINUTES_PER_WEEK],
        ["d", MINUTES_PER_DAY],
        ["h", MINUTES_PER_HOUR],
        ["m", 1],
    ];
    for (const [unit, unitMinutes] of unitEntries) {
        const unitCount = Math.floor(remainingMinutes / unitMinutes);
        if (unitCount > 0) {
            parts.push(`${unitCount}${unit}`);
            remainingMinutes -= unitCount * unitMinutes;
        }
    }
    return parts.join(" ");
};
